import { useEffect, useState } from 'react';
import {
  Button,
  Input,
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogBody,
  DialogFooter,
} from '@vnetwork/ui/primitives';
import { Field } from '@vnetwork/ui/forms';
import { Alert, useToast } from '@vnetwork/ui/feedback';
import type { SlaDowntimeLog } from '@/api/services';
import { useEndDowntimeLog } from '@/api/services-mutations';
import { formatDateTime } from '@/lib/format';

function errMsg(err: unknown): string {
  const res = (err as { response?: { data?: { error_description?: string; error?: string; message?: string } } })?.response;
  return res?.data?.error_description ?? res?.data?.error ?? res?.data?.message ?? 'Something went wrong.';
}

function toLocalInput(iso?: string | null): string {
  if (!iso) return '';
  const d = new Date(iso);
  if (Number.isNaN(d.getTime())) return '';
  const pad = (n: number) => String(n).padStart(2, '0');
  return `${d.getFullYear()}-${pad(d.getMonth() + 1)}-${pad(d.getDate())}T${pad(d.getHours())}:${pad(d.getMinutes())}`;
}

/** Close an ongoing downtime by setting its end time (`…/downtime/:id/end`). */
export function SlaEndDowntimeDialog({
  serviceId,
  downtime,
  open,
  onOpenChange,
}: {
  serviceId: number;
  downtime: SlaDowntimeLog | null;
  open: boolean;
  onOpenChange: (open: boolean) => void;
}) {
  const toast = useToast();
  const end = useEndDowntimeLog(serviceId);
  const [endTime, setEndTime] = useState('');

  useEffect(() => {
    if (open) setEndTime(toLocalInput(new Date().toISOString()));
  }, [open]);

  if (!open || !downtime) return null;

  const submit = () => {
    if (!endTime) { toast.error('Please choose an end time'); return; }
    end.mutate(
      { downtimeId: downtime.downtime_log_id, downtime_end: new Date(endTime).toISOString() },
      {
        onSuccess: () => { toast.success('Downtime ended'); onOpenChange(false); },
        onError: (e) => toast.error(errMsg(e)),
      },
    );
  };

  return (
    <Dialog open onOpenChange={(o) => !o && onOpenChange(false)}>
      <DialogContent size="sm">
        <DialogHeader>
          <DialogTitle>End downtime</DialogTitle>
        </DialogHeader>
        <DialogBody>
          <div style={{ display: 'grid', gap: 'var(--vn-space-4)' }}>
            <Alert
              tone="neutral"
              title={`Started at: ${formatDateTime(downtime.downtime_start)}`}
            >
              {downtime.downtime_description ?? undefined}
            </Alert>
            <Field label="End time" required>
              {(p) => <Input {...p} type="datetime-local" value={endTime} onChange={(e) => setEndTime(e.target.value)} />}
            </Field>
          </div>
        </DialogBody>
        <DialogFooter>
          <Button variant="ghost" onClick={() => onOpenChange(false)} disabled={end.isPending}>Cancel</Button>
          <Button variant="primary" onClick={submit} disabled={end.isPending}>Confirm end</Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}
